import { assertCompleteTranslations, locales, toolText, type Locale, type ToolId } from './i18n'
import { tools } from './tools'

export type AppRoute = {
  locale: Locale
  toolId: ToolId | null
}

export const toolIds = tools.map((tool) => tool.id as ToolId)
const toolIdSet = new Set<string>(toolIds)

assertCompleteTranslations(toolIds)

export function pathFor(locale: Locale, toolId: ToolId | null = null): string {
  const prefix = locale === 'en' ? '' : `/${locale}`
  return toolId ? `${prefix}/${toolId}` : prefix || '/'
}

export function resolveRoute(pathname: string): AppRoute | null {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '')
  const segments = normalized.split('/').filter(Boolean)
  let locale: Locale = 'en'

  if (segments[0] === 'zh' || segments[0] === 'ja') {
    locale = segments.shift() as Locale
  } else if (segments[0] === 'en') {
    return null
  }

  if (segments.length === 0) return { locale, toolId: null }
  if (segments.length !== 1 || !toolIdSet.has(segments[0])) return null
  return { locale, toolId: segments[0] as ToolId }
}

export function allRoutes(): AppRoute[] {
  return locales.flatMap((locale) => [
    { locale, toolId: null },
    ...toolIds.map((toolId) => ({ locale, toolId })),
  ])
}

export function relatedToolIds(toolId: ToolId, count = 3): ToolId[] {
  const current = tools.find((tool) => tool.id === toolId)
  if (!current) return []
  const sameKind = tools.filter((tool) => tool.kind === current.kind)
  const currentIndex = sameKind.findIndex((tool) => tool.id === toolId)
  return Array.from({ length: Math.min(count, sameKind.length - 1) }, (_, offset) => (
    sameKind[(currentIndex + offset + 1) % sameKind.length].id as ToolId
  ))
}

export function legacyHashPath(hash: string): string | null {
  const match = hash.match(/^#\/([a-z0-9-]+)$/)
  if (!match || !toolIdSet.has(match[1])) return null
  return pathFor('en', match[1] as ToolId)
}

export function routeLabel(route: AppRoute): string {
  return route.toolId ? toolText(route.locale, route.toolId).name : 'Home'
}
